'use client';

import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { useSiteData } from '@/lib/dataCache';
import OptimizedImage from '@/components/ui/OptimizedImage';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const AVATAR_COLORS = [
  'linear-gradient(135deg, #6C63FF, #8D84FF)',
  'linear-gradient(135deg, #FF6584, #FF84A2)',
  'linear-gradient(135deg, #5A50F0, #6C63FF)',
  'linear-gradient(135deg, #E85070, #FF6584)',
];

type ApiTestimonial = {
  id: string;
  name: string;
  designation: string;
  company: string;
  quote: string;
  rating: number;
  avatarUrl?: string | null;
};

function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export default function Testimonials() {
  const items = useSiteData<ApiTestimonial[]>('testimonials', '/api/testimonials', []);

  return (
    <section id="testimonials" className="testimonials-section">
      <div className="testimonials-blob testimonials-blob-1" />
      <div className="testimonials-blob testimonials-blob-2" />

      <div className="section-container">
        <motion.div
          className="section-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <span className="section-label">Testimonials</span>
          <h2 className="section-title">What Clients Say</h2>
          <p className="section-subtitle">
            Real feedback from clients and collaborators I&apos;ve had the pleasure of working with
          </p>
        </motion.div>

        <div className="testimonials-grid">
          {items.length === 0 && (
            <div className="testimonials-empty">No testimonials yet.</div>
          )}
          {items.map((t, i) => (
            <motion.div
              key={t.id}
              className="testimonial-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <div className="testimonial-quote-icon">
                <Quote size={24} />
              </div>

              <div className="testimonial-stars">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <Star
                    key={idx}
                    size={16}
                    className={
                      idx < t.rating ? 'testimonial-star testimonial-star-filled' : 'testimonial-star'
                    }
                    fill={idx < t.rating ? '#FFB400' : 'none'}
                    stroke={idx < t.rating ? '#FFB400' : '#D0D1E0'}
                  />
                ))}
              </div>

              <p className="testimonial-text">&ldquo;{t.quote}&rdquo;</p>

              <div className="testimonial-author">
                <div
                  className="testimonial-avatar"
                  style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                >
                  {t.avatarUrl ? (
                    <OptimizedImage
                      src={t.avatarUrl}
                      alt={t.name}
                      className="testimonial-avatar-img"
                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                      loading="lazy"
                    />
                  ) : (
                    getInitials(t.name)
                  )}
                </div>
                <div className="testimonial-meta">
                  <div className="testimonial-name">{t.name}</div>
                  <div className="testimonial-role">
                    {t.designation} <span className="testimonial-role-at">@</span>{' '}
                    <span className="testimonial-company">{t.company}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
